import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  ArrowTab,
  ArrowTabGroup,
  CustomTabPanel,
  PageSection,
} from '@product-framework/index';
import { SYSTEM_SETTING_FORM_VIEW } from '@product-framework/pages/ServiceProvider/constants';
import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope';
import { useLocalization } from '@ui-framework/index';
import { useNavigate, useParams } from 'react-router-dom';
import InsightSetting from './InsightSetting';
import DispatchSettings from './DispatchSetting';
import ClaimsSetting from './ClaimsSetting';
import ApiKeysSetting from './ApiKeysSetting';

const TAB_DEFINITIONS = formatMessage => [
  {
    view: SYSTEM_SETTING_FORM_VIEW.CLAIMS,
    label: formatMessage({
      id: 'SYSTEM_SETTINGS_TAB_CLAIMS',
      defaultMessage: 'Claims',
    }),
    Component: ClaimsSetting,
  },
  {
    view: SYSTEM_SETTING_FORM_VIEW.DISPATCH,
    label: formatMessage({
      id: 'SYSTEM_SETTINGS_TAB_DISPATCH',
      defaultMessage: 'Dispatch',
    }),
    Component: DispatchSettings,
  },
  {
    view: SYSTEM_SETTING_FORM_VIEW.INSIGHT,
    label: formatMessage({
      id: 'SYSTEM_SETTINGS_TAB_INSIGHT',
      defaultMessage: 'Insight',
    }),
    Component: InsightSetting,
  },
  {
    view: SYSTEM_SETTING_FORM_VIEW.APIKEY,
    label: formatMessage({
      id: 'SYSTEM_SETTINGS_TAB_API_KEY',
      defaultMessage: 'API Key',
    }),
    Component: ApiKeysSetting,
  },
];

function parseGlobalUrlIndex(raw) {
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

function SystemSettings({
  onChange = () => undefined,
  action,
  tabViews: tabViewsProp,
}) {
  const scope = useServiceProviderScope();
  const serviceProviderId = scope.serviceProviderId ?? null;
  const locationId = scope.locationId ?? null;
  const tabViews = tabViewsProp ?? scope.systemSettingsTabViews;

  const { formatMessage, messages } = useLocalization();
  const navigate = useNavigate();
  const { systemSettingsTabNumber } = useParams();

  const visibleTabs = useMemo(
    () =>
      tabViews
        .map(view =>
          TAB_DEFINITIONS(formatMessage).find(def => def.view === view),
        )
        .filter(Boolean),
    [tabViews, messages],
  );

  const globalUrlIndices = useMemo(
    () => visibleTabs.map(def => def.view - 1),
    [visibleTabs],
  );

  const resolveGlobalUrlIndex = () => {
    const fromNestedParam = parseGlobalUrlIndex(systemSettingsTabNumber);
    if (
      fromNestedParam !== null &&
      globalUrlIndices.includes(fromNestedParam)
    ) {
      return fromNestedParam;
    }

    const fromAction = parseGlobalUrlIndex(action);
    if (fromAction !== null && globalUrlIndices.includes(fromAction)) {
      return fromAction;
    }

    return globalUrlIndices[0] ?? 0;
  };

  const globalUrlIndex = resolveGlobalUrlIndex();
  const visibleTabIndex = globalUrlIndices.indexOf(globalUrlIndex);
  const selectedVisibleIndex = visibleTabIndex >= 0 ? visibleTabIndex : 0;

  const onMainTabChange = newVisibleIndex => {
    const nextGlobal = globalUrlIndices[newVisibleIndex];
    if (nextGlobal === undefined) {
      return;
    }
    navigate(scope.getTabNavigationPath(nextGlobal));
  };

  const tabCount = visibleTabs.length;

  return (
    <PageSection
      label={formatMessage({
        id: 'SERVICE_PROVIDER_MODULE_SETTINGS_HEADING',
        defaultMessage: 'System Settings',
      })}>
      <Box
        sx={{
          width: {
            xs: '100%',
            md: '40%',
          },
        }}>
        <ArrowTabGroup
          value={selectedVisibleIndex}
          onChange={(event, newValue) => {
            onMainTabChange(newValue);
          }}>
          {visibleTabs.map((def, i) => (
            <ArrowTab
              key={def.view}
              value={i}
              maxTabs={tabCount}
              arrowHeadArea={i === 0 ? 94 : undefined}
              arrowBackArea={i === tabCount - 1 ? 10 : undefined}
              label={def.label}
            />
          ))}
        </ArrowTabGroup>
      </Box>
      <Box>
        {visibleTabs.map((def, i) => {
          const { Component } = def;
          return (
            <CustomTabPanel
              key={def.view}
              value={selectedVisibleIndex}
              index={i}>
              {def.view === SYSTEM_SETTING_FORM_VIEW.APIKEY ? (
                <Component serviceProviderId={serviceProviderId} />
              ) : (
                <Component
                  serviceProviderId={serviceProviderId}
                  locationId={locationId}
                  onChange={onChange}
                />
              )}
            </CustomTabPanel>
          );
        })}
      </Box>
    </PageSection>
  );
}

export default SystemSettings;
